import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../context/I18nContext";

function LangToggle() {
  const { lang, setLang } = useI18n();
  return (
    <div data-testid="lang-toggle" className="flex items-center rounded-full border border-navy/15 overflow-hidden text-xs">
      {["es", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1.5 transition-colors ${lang === l ? "bg-navy text-cream" : "text-navy/50 hover:text-navy"}`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const LINKS = [
    { label: t("nav.ecosistema"), to: "/#ecosistema", id: "ecosistema" },
    { label: t("nav.neo"), to: "/#neo", id: "neo" },
    { label: t("nav.metodologia"), to: "/#metodologia", id: "metodologia" },
    { label: t("nav.intelligence"), to: "/#intelligence", id: "intelligence" },
    { label: t("nav.dossiers"), to: "/#dossiers", id: "dossiers" },
    { label: t("nav.insights"), to: "/insights", route: true },
    { label: "GOÛT", to: "/marca/gout", route: true },
    { label: "GOÛT READY™", to: "/gout-ready", route: true },
    { label: "VETTA", to: "/marca/vetta", route: true },
    { label: "AURA", to: "/marca/aura", route: true },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const goSection = (id) => {
    setOpen(false);
    navigate("/");
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 60);
  };

  return (
    <motion.nav
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-cream/80 border-b border-navy/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
          <img src="/brand/omnia-black.png" alt="OMNIA · Neo-Designer"
            className="h-11 w-auto object-contain transition-opacity hover:opacity-70" />
        </Link>

        <div className="hidden lg:flex items-center gap-6">
          {LINKS.map((l) =>
            l.route ? (
              <Link key={l.label} to={l.to} data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm text-navy/70 hover:text-navy transition-colors">
                {l.label}
              </Link>
            ) : (
              <a key={l.label} href={l.to} data-testid={`nav-link-${l.id}`}
                className="text-sm text-navy/70 hover:text-navy transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/");
                  setTimeout(() => document.getElementById(l.id)?.scrollIntoView({ behavior: "smooth" }), 60);
                }}>
                {l.label}
              </a>
            )
          )}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <LangToggle />
          {user ? (
            <Link to="/os" data-testid="nav-open-os"
              className="group inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-2.5 hover:opacity-90 transition-opacity">
              {t("nav.os")}
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="hidden sm:inline text-sm text-navy/70 hover:text-navy transition-colors">
                {t("nav.login")}
              </Link>
              <Link to="/login" data-testid="nav-enter-os"
                className="hidden sm:inline-flex group items-center gap-2 rounded-full bg-navy text-white text-sm px-5 py-2.5 hover:bg-navy-soft transition-colors">
                {t("nav.enter")}
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </>
          )}
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen((v) => !v)} aria-label="Menú"
            className="lg:hidden w-10 h-10 rounded-full border border-navy/15 flex items-center justify-center text-navy hover:bg-navy/5 transition-colors">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            data-testid="nav-mobile-menu"
            className="lg:hidden overflow-hidden backdrop-blur-xl bg-cream/95 border-b border-navy/10">
            <div className="px-6 py-6 flex flex-col">
              {LINKS.map((l, i) => (
                <motion.div key={l.label}
                  initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.04 + i * 0.03 }}>
                  {l.route ? (
                    <Link to={l.to} onClick={() => setOpen(false)} data-testid={`nav-m-link-${l.label.toLowerCase()}`}
                      className="block py-3 text-lg text-navy/80 hover:text-navy border-b border-navy/5 transition-colors">
                      {l.label}
                    </Link>
                  ) : (
                    <button onClick={() => goSection(l.id)} data-testid={`nav-m-link-${l.id}`}
                      className="block w-full text-left py-3 text-lg text-navy/80 hover:text-navy border-b border-navy/5 transition-colors">
                      {l.label}
                    </button>
                  )}
                </motion.div>
              ))}
              {!user && (
                <Link to="/login" onClick={() => setOpen(false)} data-testid="nav-m-enter"
                  className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white px-5 py-3.5 hover:bg-navy-soft transition-colors">
                  {t("nav.enter")} <ArrowUpRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
